import { type FC, useContext } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthContext } from "../hooks/authProvider/authProvider";
import AdminDashboardPageContainer from "../routes/admin/AdminDashboardPage/index";
import {
  ContactDetailPage,
  ContactFormPage,
  ContactsPage,
} from "../routes/admin/distribution/contacts";
import {
  AddContactsPage,
  GroupDetailPage,
  GroupFormPage,
  GroupsPage,
} from "../routes/admin/distribution/groups";
import { FeatureFlagsPage } from "../routes/admin/featureFlags";
import { SkillMastersPage } from "../routes/admin/masters/skills";
import { TenantDetailPage, TenantsPage } from "../routes/admin/tenants";
import { UserDetailPage, UsersPage } from "../routes/admin/users";
import { canAccessAdminPages } from "../utils/admin-utils";
import { isTenantAdmin } from "../utils/authority-utils";

/**
 * 管理者権限を持つユーザーのみアクセス可能なルートを保護し、
 * 管理者向けのルーティングも担当するコンポーネント
 *
 * - SystemAdmin (isSystemAdmin): 全ルートにアクセス可能
 * - TenantAdmin (owner/admin): 配信グループ・連絡先管理のみアクセス可能
 * @return {JSX.Element} 管理者向けルート保護・ルーティングコンポーネント
 */
export const AdminRoute: FC = () => {
  const { currentUser, isLoadingUser } = useContext(AuthContext);

  // ローディング中またはcurrentUserが取得できていない場合は待機
  if (isLoadingUser || !currentUser) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">管理者権限を確認中...</div>
      </div>
    );
  }

  const isSystemAdmin = canAccessAdminPages(currentUser);
  const isTenantAdminUser = isTenantAdmin(currentUser);

  // SystemAdminでもTenantAdminでもない場合はリダイレクト
  if (!isSystemAdmin && !isTenantAdminUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Routes>
      {/* SystemAdmin専用ルート */}
      {isSystemAdmin && (
        <Route path="/" element={<AdminDashboardPageContainer />} />
      )}
      {isSystemAdmin && <Route path="/tenants" element={<TenantsPage />} />}
      {isSystemAdmin && (
        <Route path="/tenants/:id" element={<TenantDetailPage />} />
      )}
      {isSystemAdmin && <Route path="/users" element={<UsersPage />} />}
      {isSystemAdmin && (
        <Route path="/users/:id" element={<UserDetailPage />} />
      )}
      {isSystemAdmin && (
        <Route path="/feature-flags" element={<FeatureFlagsPage />} />
      )}
      {isSystemAdmin && (
        <Route path="/masters/skills" element={<SkillMastersPage />} />
      )}
      {/* Distribution routes — TenantAdmin (owner/admin) 以上でアクセス可能 */}
      <Route path="/distribution/groups" element={<GroupsPage />} />
      <Route path="/distribution/groups/new" element={<GroupFormPage />} />
      <Route path="/distribution/groups/:id" element={<GroupDetailPage />} />
      <Route path="/distribution/groups/:id/edit" element={<GroupFormPage />} />
      <Route
        path="/distribution/groups/:id/add-contacts"
        element={<AddContactsPage />}
      />
      <Route path="/distribution/contacts" element={<ContactsPage />} />
      <Route path="/distribution/contacts/new" element={<ContactFormPage />} />
      <Route
        path="/distribution/contacts/:id"
        element={<ContactDetailPage />}
      />
      <Route
        path="/distribution/contacts/:id/edit"
        element={<ContactFormPage />}
      />
      {/* 未定義パスへのフォールバック */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

export default AdminRoute;
